import { ErrorPage, LoginPage, RecoveryPage, RegistrationPage, VerificationPage } from "@payezy/auth";
import { getMeOptions } from "@payezy/api-client/public/query";
import { Alert, Button, Spinner, Toaster } from "@payezy/ui";
import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { Brand } from "./components/Brand";
import { AppShell } from "./components/AppShell";
import { handleAuthProblem, isProblem, kratos, problemMessage } from "./lib/api";
import { AcceptInvitationPage } from "./pages/AcceptInvitation";
import { ActivityPage } from "./pages/Activity";
import { OverviewPage } from "./pages/Overview";
import { OrgSettingsPage } from "./pages/OrgSettings";
import { SecurityPage } from "./pages/Security";
import { TeamPage } from "./pages/Team";
import { ViewAsEndedPage, ViewAsStartPage } from "./pages/ViewAs";
import { WelcomePage } from "./pages/Welcome";
import { extraOrgRoutes } from "./routes.extra";

type Ctx = { queryClient: QueryClient };

const links = {
  login: "/auth/login",
  registration: "/auth/registration",
  recovery: "/auth/recovery",
  verification: "/auth/verification",
};

const rootRoute = createRootRouteWithContext<Ctx>()({
  component: () => (
    <>
      <Outlet />
      <Toaster position="top-right" richColors />
    </>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-md p-12">
      <Alert tone="danger" title="Something went wrong">{problemMessage(error)}</Alert>
      <Button className="mt-4" variant="secondary" onClick={() => window.location.reload()}>Reload</Button>
    </div>
  ),
  pendingComponent: () => (
    <div className="flex h-dvh items-center justify-center"><Spinner /></div>
  ),
});

const authRoute = (path: string, Component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => rootRoute, path, component: Component });

const loginRoute = authRoute("/auth/login", () => <LoginPage kratos={kratos} brand={<Brand />} links={links} mfaSetupUrl="/settings/security" />);
const registrationRoute = authRoute("/auth/registration", () => <RegistrationPage kratos={kratos} brand={<Brand />} links={links} />);
const recoveryRoute = authRoute("/auth/recovery", () => <RecoveryPage kratos={kratos} brand={<Brand />} links={links} />);
const verificationRoute = authRoute("/auth/verification", () => <VerificationPage kratos={kratos} brand={<Brand />} links={links} />);
const errorRoute = authRoute("/auth/error", () => <ErrorPage kratos={kratos} brand={<Brand />} homeUrl="/" />);
const viewAsRoute = authRoute("/view-as", ViewAsStartPage);
const viewAsEndedRoute = authRoute("/view-as/ended", ViewAsEndedPage);

/** Everything below requires a verified session; /v1/me drives the login gates. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(getMeOptions());
    } catch (e) {
      if (handleAuthProblem(e)) return new Promise(() => {});
      throw e;
    }
  },
  component: () => <Outlet />,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getMeOptions());
    const first = me.memberships[0];
    throw first ? redirect({ to: "/o/$orgId", params: { orgId: first.org_id } }) : redirect({ to: "/welcome" });
  },
});

const welcomeRoute = createRoute({ getParentRoute: () => appRoute, path: "/welcome", component: WelcomePage });
const invitationRoute = createRoute({ getParentRoute: () => appRoute, path: "/invitations/accept", component: AcceptInvitationPage });
const securityRoute = createRoute({ getParentRoute: () => appRoute, path: "/settings/security", component: SecurityPage });

export const orgRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/o/$orgId",
  beforeLoad: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(getMeOptions());
    if (!me.memberships.some((m) => m.org_id === params.orgId)) throw redirect({ to: "/" });
  },
  component: AppShell,
});

const orgIndexRoute = createRoute({ getParentRoute: () => orgRoute, path: "/", component: OverviewPage });
const teamRoute = createRoute({ getParentRoute: () => orgRoute, path: "/team", component: TeamPage });
const orgSettingsRoute = createRoute({ getParentRoute: () => orgRoute, path: "/settings", component: OrgSettingsPage });
const activityRoute = createRoute({ getParentRoute: () => orgRoute, path: "/activity", component: ActivityPage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  registrationRoute,
  recoveryRoute,
  verificationRoute,
  errorRoute,
  viewAsRoute,
  viewAsEndedRoute,
  appRoute.addChildren([
    indexRoute,
    welcomeRoute,
    invitationRoute,
    securityRoute,
    orgRoute.addChildren([orgIndexRoute, teamRoute, orgSettingsRoute, activityRoute, ...extraOrgRoutes(orgRoute)]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent", defaultPendingMs: 150 });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}

export { isProblem };
